import { useState } from 'react';

import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Eye,
  FileSearch,
  StopCircle,
} from 'lucide-react';
import { useParams, Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCampaignReviews, useStopReview } from '@/hooks/useReviews';
import { formatDateTime } from '@/utils/format';

const REVIEW_STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    color: 'bg-gray-100 text-gray-800',
    icon: Clock,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-800',
    icon: Loader2,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-100 text-emerald-800',
    icon: CheckCircle2,
  },
  FAILED: {
    label: 'Failed',
    color: 'bg-rose-100 text-rose-800',
    icon: XCircle,
  },
  STOPPED: {
    label: 'Stopped',
    color: 'bg-amber-100 text-amber-800',
    icon: StopCircle,
  },
};

export const CampaignReviewHistoryPage = () => {
  usePageTitle('Campaign Review History');
  const { campaignId } = useParams<{ campaignId: string }>();
  const { data: reviews, isLoading } = useCampaignReviews(campaignId);
  const stopReview = useStopReview();
  const [stopConfirmId, setStopConfirmId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-100 items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading review history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">AI Review</p>
          <h1 className="text-3xl font-bold tracking-tight">Review History</h1>
          {reviews?.[0] && (
            <p className="text-muted-foreground">Campaign: {reviews[0]?.campaign.name}</p>
          )}
        </div>
        <Button variant="outline" asChild>
          <Link to={campaignId ? `/campaigns/${campaignId}` : '/reviews'}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
      </header>

      {!reviews || reviews.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileSearch className="text-muted-foreground mb-4 h-16 w-16" />
            <h3 className="mb-2 text-xl font-semibold">No Reviews Yet</h3>
            <p className="text-muted-foreground mb-6 text-center">
              This campaign hasn&apos;t been reviewed yet. Start your first review to see results
              here.
            </p>
            <Button asChild>
              <Link to={`/campaigns/${campaignId}`}>Start Review</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => {
            const statusConfig = REVIEW_STATUS_CONFIG[review.status];
            const StatusIcon = statusConfig.icon;
            const accuracy =
              review.total_calls > 0
                ? Math.round(
                    ((review.total_calls - (review.calls_with_issues || 0)) / review.total_calls) *
                      100,
                  )
                : 0;

            return (
              <Card key={review.id} className="transition-shadow hover:shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <CardTitle className="text-lg">
                          Review - {formatDateTime(review.created_at)}
                        </CardTitle>
                        <Badge className={statusConfig.color}>
                          <StatusIcon
                            className={`mr-1 h-3 w-3 ${review.status === 'IN_PROGRESS' ? 'animate-spin' : ''}`}
                          />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <CardDescription>
                        Started on {formatDateTime(review.created_at)}
                        {review.completed_at &&
                          ` • Completed on ${formatDateTime(review.completed_at)}`}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="grid grid-cols-4 gap-6">
                      <div>
                        <p className="text-muted-foreground text-sm">Total Calls</p>
                        <p className="text-2xl font-bold">{review.total_calls}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">Reviewed</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {review.reviewed_calls || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">Issues Found</p>
                        <p className="text-2xl font-bold text-amber-600">
                          {review.calls_with_issues || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">Accuracy</p>
                        <p
                          className={`text-2xl font-bold ${
                            accuracy >= 90
                              ? 'text-emerald-600'
                              : accuracy >= 70
                                ? 'text-amber-600'
                                : 'text-rose-600'
                          }`}
                        >
                          {accuracy}%
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(review.status === 'IN_PROGRESS' || review.status === 'PENDING') && (
                        <Button
                          variant="destructive"
                          onClick={() => setStopConfirmId(review.id)}
                          disabled={stopReview.isPending}
                        >
                          <StopCircle className="mr-2 h-4 w-4" />
                          Stop Review
                        </Button>
                      )}
                      {(review.status === 'COMPLETED' || review.status === 'STOPPED') && (
                        <Button asChild>
                          <Link to={`/reviews/campaign-review/${review.id}`}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!stopConfirmId} onOpenChange={() => setStopConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop Review</DialogTitle>
            <DialogDescription>
              Are you sure you want to stop this review? Calls that have already been reviewed will
              be preserved, but remaining calls will not be processed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={stopReview.isPending}
              onClick={() => {
                if (stopConfirmId) {
                  stopReview.mutate(stopConfirmId, {
                    onSettled: () => setStopConfirmId(null),
                  });
                }
              }}
            >
              {stopReview.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Stopping...
                </>
              ) : (
                'Stop Review'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
